/**
 * Minimal button primitive used by migrated portal pages.
 *
 * Spec: PORTAL_MIGRATION_FROM_PSM_SSOT.md §4.1.2 (Phase 1 foundation).
 * customer-portal/web does not yet use Tailwind, so this is an inline-
 * style adaptation of PSM `components/ui/button.tsx` (shadcn-derived).
 * Variants are kept small on purpose — Phase 2-4 page-migration PRs
 * should reach for `primary` / `secondary` / `ghost` / `danger` and
 * extend here only if a real need arises.
 */
import type { ButtonHTMLAttributes, ReactNode } from 'react';

type Variant = 'primary' | 'secondary' | 'ghost' | 'danger';
type Size = 'sm' | 'md' | 'lg';

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: Variant;
  size?: Size;
  /** When true, shrink to the natural width of children. Default: false. */
  inline?: boolean;
  children?: ReactNode;
}

const BASE_STYLE: React.CSSProperties = {
  border: '1px solid transparent',
  borderRadius: '0.375rem',
  cursor: 'pointer',
  fontFamily: 'inherit',
  fontWeight: 500,
  lineHeight: 1.2,
  transition: 'background-color 100ms, border-color 100ms, opacity 100ms',
};

const SIZE_STYLE: Record<Size, React.CSSProperties> = {
  sm: { padding: '0.375rem 0.75rem', fontSize: '0.8125rem' },
  md: { padding: '0.5rem 1rem', fontSize: '0.875rem' },
  lg: { padding: '0.75rem 1.25rem', fontSize: '1rem' },
};

const VARIANT_STYLE: Record<Variant, React.CSSProperties> = {
  primary: { background: '#1f4ed8', color: '#fff', borderColor: '#1e40af' },
  secondary: { background: '#fff', color: '#111827', borderColor: '#d1d5db' },
  ghost: { background: 'transparent', color: '#1f4ed8', borderColor: 'transparent' },
  danger: { background: '#dc2626', color: '#fff', borderColor: '#b91c1c' },
};

export function Button({
  variant = 'primary',
  size = 'md',
  inline = false,
  disabled,
  style,
  children,
  ...rest
}: ButtonProps): JSX.Element {
  const composed: React.CSSProperties = {
    ...BASE_STYLE,
    ...SIZE_STYLE[size],
    ...VARIANT_STYLE[variant],
    opacity: disabled ? 0.55 : 1,
    cursor: disabled ? 'not-allowed' : BASE_STYLE.cursor,
    width: inline ? 'auto' : undefined,
    ...style,
  };
  return (
    <button type="button" disabled={disabled} style={composed} {...rest}>
      {children}
    </button>
  );
}
